import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StudentAvatar from "@/components/StudentAvatar";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Search, ArrowUpDown, AlertTriangle, Trophy, Filter, FileDown,
  Users, TrendingDown, Award, ClipboardCheck, GraduationCap,
} from "lucide-react";
import { YEAR_LABEL } from "@/lib/school";

const STATUS_STYLES = {
  attivo: "bg-emerald-50 text-emerald-800 border-emerald-200",
  sospeso: "bg-orange-50 text-orange-800 border-orange-200",
  ritirato: "bg-stone-200 text-stone-700 border-stone-300",
  diplomato: "bg-indigo-50 text-indigo-800 border-indigo-200",
};

const SORT_KEYS = {
  name: (s) => (s.last_name || "").toLowerCase(),
  matricola: (s) => s.matricola || "",
  attendance: (s) => s.attendance_rate,
  cfu: (s) => s.cfu_total,
  media: (s) => s.media_aritmetica ?? -1,
  exams: (s) => s.exams_passed,
};

export default function StudentsStatistics() {
  const navigate = useNavigate();
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["students-summary"],
    queryFn: async () => (await api.get("/school-stats/students-summary")).data,
    refetchInterval: 60_000,
  });

  const [search, setSearch] = useState("");
  const [fYear, setFYear] = useState("all");
  const [fTrack, setFTrack] = useState("all");
  const [fStatus, setFStatus] = useState("attivo");
  const [fFlag, setFFlag] = useState("all"); // all | risk | top
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const filtered = useMemo(() => {
    let r = rows;
    const q = search.trim().toLowerCase();
    if (q) {
      r = r.filter((s) =>
        `${s.first_name || ""} ${s.last_name || ""}`.toLowerCase().includes(q)
        || (s.matricola || "").toLowerCase().includes(q)
        || (s.email || "").toLowerCase().includes(q)
      );
    }
    if (fYear !== "all") r = r.filter((s) => String(s.year_level) === fYear);
    if (fTrack !== "all") r = r.filter((s) => s.track_type === fTrack);
    if (fStatus !== "all") r = r.filter((s) => s.status === fStatus);
    if (fFlag === "risk") r = r.filter((s) => s.at_risk_attendance);
    if (fFlag === "top") r = r.filter((s) => s.top_performer);
    const key = SORT_KEYS[sortKey] || SORT_KEYS.name;
    r = [...r].sort((a, b) => {
      const va = key(a); const vb = key(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return r;
  }, [rows, search, fYear, fTrack, fStatus, fFlag, sortKey, sortDir]);

  const kpis = useMemo(() => {
    if (!rows.length) return { total: 0, risk: 0, top: 0, avgCfu: 0, avgAtt: 0 };
    const risk = rows.filter((s) => s.at_risk_attendance).length;
    const top = rows.filter((s) => s.top_performer).length;
    const avgCfu = Math.round(rows.reduce((s, x) => s + x.cfu_total, 0) / rows.length * 10) / 10;
    const withAtt = rows.filter((s) => s.attendance_total > 0);
    const avgAtt = withAtt.length ? Math.round(withAtt.reduce((s, x) => s + x.attendance_rate, 0) / withAtt.length) : 0;
    return { total: rows.length, risk, top, avgCfu, avgAtt };
  }, [rows]);

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "name" || key === "matricola" ? "asc" : "desc");
    }
  }

  function exportCsv() {
    const header = ["Matricola", "Cognome", "Nome", "Anno", "Percorso", "Status", "Presenze %", "CFU tot", "CFU esami", "CFU pratici", "Media arit.", "Media pond.", "Esami sup.", "Esami non sup.", "Appelli prenotati"];
    const lines = [header.join(";")];
    for (const s of filtered) {
      lines.push([
        s.matricola || "", s.last_name || "", s.first_name || "",
        s.year_level || "", s.track_type || "", s.status || "",
        s.attendance_rate, s.cfu_total, s.cfu_from_exams, s.cfu_practical,
        s.media_aritmetica ?? "", s.media_ponderata ?? "",
        s.exams_passed, s.exams_failed, s.upcoming_bookings,
      ].map((v) => String(v).replace(/;/g, ",")).join(";"));
    }
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `studenti-statistiche-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1600px] mx-auto">
      <PageHeader
        title="Statistiche studenti"
        subtitle="Vista comparativa per fare check rapido su presenze, CFU, media e appelli. Ordina e filtra per intercettare situazioni da seguire."
        actions={
          <Button
            variant="outline"
            data-testid="stats-export-csv"
            onClick={exportCsv}
            className="rounded-full"
          >
            <FileDown size={13} className="mr-1.5"/> Esporta CSV
          </Button>
        }
      />

      {/* KPI banner */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Kpi icon={Users} label="Studenti" value={kpis.total} testId="stats-kpi-total"/>
        <Kpi icon={GraduationCap} label="CFU medi" value={kpis.avgCfu} sub="su 100" testId="stats-kpi-avg-cfu"/>
        <Kpi icon={ClipboardCheck} label="Presenza media" value={`${kpis.avgAtt}%`} testId="stats-kpi-avg-att"/>
        <Kpi icon={Trophy} label="Top performer" value={kpis.top} sub="media ≥ 27" testId="stats-kpi-top" color="text-amber-600"/>
        <Kpi icon={AlertTriangle} label="A rischio" value={kpis.risk} sub="pres. < 70%" testId="stats-kpi-risk" color="text-red-600"/>
      </div>

      <Card className="p-3 mb-4 border border-stone-200 rounded-xl shadow-none">
        <div className="flex flex-col md:flex-row md:items-end gap-2">
          <div className="flex-1 relative">
            <Search size={14} className="absolute left-3 top-3 text-stone-400"/>
            <Input
              data-testid="stats-search"
              placeholder="Cerca per nome, matricola o email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={fYear} onValueChange={setFYear}>
            <SelectTrigger className="md:w-32" data-testid="stats-filter-year"><SelectValue placeholder="Anno"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli anni</SelectItem>
              <SelectItem value="1">I anno</SelectItem>
              <SelectItem value="2">II anno</SelectItem>
              <SelectItem value="3">III anno</SelectItem>
            </SelectContent>
          </Select>
          <Select value={fTrack} onValueChange={setFTrack}>
            <SelectTrigger className="md:w-36" data-testid="stats-filter-track"><SelectValue placeholder="Percorso"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i percorsi</SelectItem>
              <SelectItem value="residenziale">Residenziale</SelectItem>
              <SelectItem value="modulare">Modulare</SelectItem>
            </SelectContent>
          </Select>
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="md:w-32" data-testid="stats-filter-status"><SelectValue placeholder="Status"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="attivo">Attivi</SelectItem>
              <SelectItem value="sospeso">Sospesi</SelectItem>
              <SelectItem value="diplomato">Diplomati</SelectItem>
              <SelectItem value="ritirato">Ritirati</SelectItem>
            </SelectContent>
          </Select>
          <Select value={fFlag} onValueChange={setFFlag}>
            <SelectTrigger className="md:w-40" data-testid="stats-filter-flag"><SelectValue placeholder="Flag"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="risk">A rischio</SelectItem>
              <SelectItem value="top">Top performer</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {isLoading ? (
        <div className="p-10 text-sm text-stone-400 text-center">Caricamento…</div>
      ) : (
        <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FDFBF7] border-b border-stone-200">
                <tr className="text-[10px] uppercase tracking-widest text-stone-500">
                  <Th onClick={() => toggleSort("name")} active={sortKey === "name"} dir={sortDir}>Studente</Th>
                  <Th onClick={() => toggleSort("matricola")} active={sortKey === "matricola"} dir={sortDir}>Matricola</Th>
                  <th className="text-left py-3 px-3">Anno / Percorso</th>
                  <Th onClick={() => toggleSort("attendance")} active={sortKey === "attendance"} dir={sortDir}>Presenze</Th>
                  <Th onClick={() => toggleSort("cfu")} active={sortKey === "cfu"} dir={sortDir}>CFU</Th>
                  <Th onClick={() => toggleSort("media")} active={sortKey === "media"} dir={sortDir}>Media</Th>
                  <Th onClick={() => toggleSort("exams")} active={sortKey === "exams"} dir={sortDir}>Esami</Th>
                  <th className="text-left py-3 px-3">Permessi</th>
                  <th className="text-left py-3 px-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} className="p-10 text-center text-stone-400">Nessuno studente corrisponde ai filtri.</td>
                  </tr>
                )}
                {filtered.map((s) => (
                  <tr
                    key={s.id}
                    data-testid={`stats-row-${s.id}`}
                    onClick={() => navigate(`/scuola/studenti/${s.id}/statistiche`)}
                    className="border-b border-stone-100 hover:bg-stone-50/60 cursor-pointer"
                  >
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-3">
                        <StudentAvatar studentId={s.id} photoPath={s.photo_path} name={`${s.first_name || ""} ${s.last_name || ""}`} testId={`stats-avatar-${s.id}`}/>
                        <div className="min-w-0">
                          <div className="font-medium truncate">
                            {s.last_name} {s.first_name}
                            {s.top_performer && <Trophy size={11} className="inline ml-1.5 text-amber-500"/>}
                            {s.at_risk_attendance && <AlertTriangle size={11} className="inline ml-1.5 text-red-500"/>}
                          </div>
                          {s.email && <div className="text-[10px] text-stone-500 truncate">{s.email}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 mono text-xs text-stone-600">{s.matricola || "—"}</td>
                    <td className="py-3 px-3 text-xs">
                      {s.year_level ? YEAR_LABEL[s.year_level] : "—"}
                      <div className="text-[10px] text-stone-500 capitalize">{s.track_type || "—"}</div>
                    </td>
                    <td className="py-3 px-3">
                      <MetricBar
                        value={s.attendance_rate}
                        max={100}
                        color={s.attendance_rate >= 85 ? "#16a34a" : s.attendance_rate >= 70 ? "#f59e0b" : "#dc2626"}
                        label={s.attendance_total > 0 ? `${s.attendance_rate.toFixed(0)}%` : "—"}
                        sub={s.attendance_total > 0 ? `${s.attendance_total} sess.` : ""}
                      />
                    </td>
                    <td className="py-3 px-3">
                      <MetricBar
                        value={s.cfu_total}
                        max={s.cfu_target}
                        color="#1C352D"
                        label={`${s.cfu_total}/${s.cfu_target}`}
                        sub={`${s.cfu_pct}%`}
                      />
                    </td>
                    <td className="py-3 px-3">
                      {s.media_aritmetica != null ? (
                        <div>
                          <div className={`font-semibold ${s.media_aritmetica >= 27 ? "text-emerald-700" : s.media_aritmetica >= 24 ? "text-[#1C352D]" : "text-stone-600"}`}>
                            {s.media_aritmetica.toFixed(2)}
                          </div>
                          {s.media_ponderata != null && s.media_ponderata !== s.media_aritmetica && (
                            <div className="text-[10px] text-stone-500">pond. {s.media_ponderata.toFixed(2)}</div>
                          )}
                        </div>
                      ) : <span className="text-stone-400">—</span>}
                    </td>
                    <td className="py-3 px-3 text-xs">
                      <div><span className="font-medium text-emerald-700">{s.exams_passed}</span> sup.</div>
                      {s.exams_failed > 0 && <div className="text-red-700 text-[10px]">{s.exams_failed} non sup.</div>}
                      {s.upcoming_bookings > 0 && <div className="text-[10px] text-stone-500">{s.upcoming_bookings} prenotati</div>}
                    </td>
                    <td className="py-3 px-3 text-xs">
                      {(s.leave_hours_total || 0) > 0 ? (
                        <div>
                          <div className={`font-semibold ${s.leave_hours_available <= 0 ? "text-red-700" : s.leave_hours_available < s.leave_hours_total * 0.3 ? "text-amber-700" : "text-emerald-700"}`}>
                            {s.leave_hours_available}h
                          </div>
                          <div className="text-[10px] text-stone-500">su {s.leave_hours_total}h · usate {s.leave_hours_used}h</div>
                        </div>
                      ) : <span className="text-stone-400">—</span>}
                    </td>
                    <td className="py-3 px-3">
                      <Badge className={`rounded-full text-[10px] border ${STATUS_STYLES[s.status] || STATUS_STYLES.attivo}`}>{s.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length > 0 && (
            <div className="p-3 border-t border-stone-100 text-xs text-stone-500 text-right">
              {filtered.length} student{filtered.length === 1 ? "e" : "i"} · click su una riga per dettaglio statistiche
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function Th({ children, onClick, active, dir }) {
  return (
    <th className="text-left py-3 px-3 select-none">
      <button
        onClick={onClick}
        className={`inline-flex items-center gap-1 uppercase tracking-widest text-[10px] ${active ? "text-[#1C352D]" : "text-stone-500 hover:text-stone-700"}`}
      >
        {children}
        <ArrowUpDown size={9} className={active ? "opacity-100" : "opacity-40"}/>
      </button>
    </th>
  );
}

function Kpi({ icon: Icon, label, value, sub, testId, color = "text-[#1C352D]" }) {
  return (
    <Card className="p-4 border border-stone-200 rounded-xl shadow-none" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5">
        <Icon size={11}/> {label}
      </div>
      <div className={`text-2xl font-semibold mt-1 ${color}`}>{value}</div>
      {sub && <div className="text-[10px] text-stone-500 mt-0.5">{sub}</div>}
    </Card>
  );
}

function MetricBar({ value, max, color, label, sub }) {
  const pct = max ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="min-w-[100px]">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs font-medium">{label}</span>
        {sub && <span className="text-[10px] text-stone-500 ml-2">{sub}</span>}
      </div>
      <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }}/>
      </div>
    </div>
  );
}
